import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	ReleaseHistory,
} from '../../../shared/types/collection'
import { localCalendarDate } from '../../../shared/utils/calendarDate'
import { getReleaseDetails } from '../../api/collection'
import { addListen, getHistoryById } from '../../api/history'
import { getSessionSignal } from '../../api/requestSession'
import { getSettings } from '../../api/settings'
import {
	getCachedReleaseByInstanceId,
	getCachedReleaseByReleaseId,
	updateCachedReleaseByInstanceId,
	updateCachedReleaseByReleaseId,
} from '../../stores/collectionStore'
import initDialog from '../Dialog'
import type { OverlayView } from '../Overlay'
import {
	renderHistoryList,
	renderListenedDialog,
	renderReleaseInfoList,
	renderTracklist,
} from './ReleaseRenderers'
import { hideDurationOptionsTemplate } from './releaseDialogOptionTemplates'
import {
	listenDateErrorTemplate,
	releaseTitleTemplate,
} from './releaseEditTemplates'
import { emitReleaseUpdated } from './releaseEvents'
import { releaseYearLoadingTemplate } from './releaseTemplates'

type DetailStatus = 'ok' | 'loading' | 'error' | 'none'

const DETAIL_STATUS_SELECTORS: Record<DetailStatus, string> = {
	ok: '.detail-status-container.status-ok',
	loading: '.detail-status-container.status-loading',
	error: '.detail-status-container.status-error',
	none: '.detail-status-container.status-none',
}
const MASTER_YEAR_POLL_ATTEMPTS = 10
const MASTER_YEAR_POLL_INTERVAL_MS = 3000

const hydratingReleases = new Map<number, AbortSignal>()
const pollingMasterYears = new Map<number, AbortSignal>()
const releaseInfoSignatures = new WeakMap<HTMLElement, string>()
const tracklistSignatures = new WeakMap<HTMLElement, string>()

function normalizeHideDurationSelection(
	months: number | null | undefined,
): number | 'forever' | null {
	return months === -1 ? 'forever' : (months ?? null)
}

export function initializeReleaseDetails(
	release: ClientRelease,
	releaseEl: HTMLElement,
): void {
	const session = getSessionSignal()
	rememberRenderedDetails(releaseEl, release)

	if (release.hasDetails && release.detailsStatus === 'ok') {
		updateDetailStatus(releaseEl, 'ok')
		return
	}

	const loadDetails = async () => {
		if (session.aborted || !releaseMatches(releaseEl, release)) return
		updateDetailStatus(releaseEl, 'loading')
		try {
			const details = await getReleaseDetails(
				release.data?.id ?? release.id,
			)
			if (session.aborted || !releaseMatches(releaseEl, release)) return
			if (!details) {
				console.error('Failed to load release details: empty response')
				updateDetailStatus(releaseEl, 'error')
				return
			}

			const mergedRelease: ClientRelease = {
				...release,
				data: { ...release.data, ...details },
				hasDetails: true,
				detailsStatus: 'ok',
			}

			const cached = applyReleaseDataUpdate(mergedRelease)
			Object.assign(release, cached)
			if (cached.data.master_year_status === 'loading') {
				void pollReleaseMasterYear(cached)
			}
			if (releaseMatches(releaseEl, release)) {
				updateDetailStatus(releaseEl, 'ok')
			}
		} catch (error) {
			if (session.aborted) return
			console.error('Failed to load release details', error)
			if (releaseMatches(releaseEl, release)) {
				updateDetailStatus(releaseEl, 'error')
			}
		}
	}

	releaseEl
		.querySelector<HTMLElement>(DETAIL_STATUS_SELECTORS.error)
		?.querySelector('button')
		?.addEventListener('click', () => {
			void loadDetails()
		})
	releaseEl
		.querySelector<HTMLElement>(DETAIL_STATUS_SELECTORS.none)
		?.querySelector('button')
		?.addEventListener('click', () => {
			void loadDetails()
		})

	if (
		!release.hasDetails ||
		release.detailsStatus === 'error' ||
		release.detailsStatus === 'stale'
	) {
		void loadDetails()
	}
}

export async function handleListenButtonClick(
	release: ClientRelease,
): Promise<void> {
	const releaseEl = document.getElementById('release')
	const releaseId = Number(releaseEl?.getAttribute('data-release-id') ?? 0)
	const releaseInstanceId = releaseEl?.getAttribute(
		'data-release-instance-id',
	)
	const settings = await getSettings()
	const defaultHideDurationMonths = normalizeHideDurationSelection(
		settings?.defaultHideDurationMonths,
	)

	initDialog({
		id: 'release-listen-dialog',
		cancelText: t('dialog.cancel'),
		confirmText: t('dialog.save'),
		content: renderListenedDialog(
			hideDurationOptionsTemplate(defaultHideDurationMonths),
		),
		onConfirm: async (formData) => {
			const unknownDate = formData?.has('unknown-date') ?? false
			const dateValue = formData?.get('date') as string | null
			const date = !unknownDate && dateValue ? new Date(dateValue) : null
			const hideDurationValue =
				(formData?.get('hide-duration') as string) ?? ''
			const hideDurationMonths =
				hideDurationValue === 'forever'
					? -1
					: hideDurationValue
						? Number(hideDurationValue)
						: null

			document.getElementById('listen-date-error')?.remove()

			if (dateValue && date && dateValue > localCalendarDate()) {
				const errorContainer = document.getElementById(
					'listen-date-error-container',
				)
				if (errorContainer) {
					errorContainer.innerHTML = listenDateErrorTemplate(
						t('release.futureDateError'),
					)
				}
				return Promise.reject(new Error(t('release.futureDateError')))
			}

			await addListen(releaseId, date, hideDurationMonths, unknownDate)

			const updatedHistoryDoc = await getHistoryById(releaseId)
			const updatedHistory: ReleaseHistory = {
				...(release.history ?? ({} as ReleaseHistory)),
				...(updatedHistoryDoc as ReleaseHistory),
			}

			applyReleaseHistoryUpdate(
				release,
				updatedHistory,
				releaseInstanceId ?? releaseId,
			)
		},
		title: t('release.listenTitle'),
	})

	const unknownDateInput = document.getElementById(
		'listen-unknown-date',
	) as HTMLInputElement | null
	const dateInput = document.getElementById(
		'listen-date',
	) as HTMLInputElement | null
	const syncDateInput = () => {
		if (!dateInput || !unknownDateInput) return
		dateInput.disabled = unknownDateInput.checked
		dateInput.required = !unknownDateInput.checked
	}
	unknownDateInput?.addEventListener('change', syncDateInput)
	syncDateInput()
}

export async function hydrateRelease(
	releaseId: number,
	instanceId: number | undefined,
	fallback:
		| {
				date_added?: string
				history?: ReleaseHistory | null
				isFavorite?: boolean
				rating?: number | null
		  }
		| undefined,
	renderRelease: (release: ClientRelease, dimmed?: boolean) => OverlayView,
): Promise<void> {
	const session = getSessionSignal()
	if (hydratingReleases.get(releaseId) === session) return
	hydratingReleases.set(releaseId, session)

	try {
		const details = await getReleaseDetails(releaseId)
		if (session.aborted || !details) return

		const base =
			instanceId !== undefined
				? getCachedReleaseByInstanceId(instanceId)
				: getCachedReleaseByReleaseId(releaseId)
		const hydratedDetails = details as ClientRelease['data']
		const hydrated: ClientRelease = {
			...(base ?? {}),
			id: base?.id ?? instanceId ?? releaseId,
			data: { ...(base?.data ?? {}), ...hydratedDetails },
			date_added: base?.date_added ?? fallback?.date_added ?? '',
			isFavorite: base?.isFavorite ?? fallback?.isFavorite ?? false,
			rating: base?.rating ?? fallback?.rating ?? null,
			history: base?.history ?? fallback?.history ?? null,
			hasDetails: true,
			detailsStatus: 'ok',
		}
		const cached =
			instanceId !== undefined || base
				? updateCachedReleaseByInstanceId(
						hydrated,
						base?.id ?? instanceId,
					)
				: updateCachedReleaseByReleaseId(hydrated, releaseId)
		const renderedRelease = cached ?? hydrated
		const rendered = renderRelease(renderedRelease)
		const placeholderEl = document.getElementById('release')

		if (
			!placeholderEl ||
			!releaseMatchesIds(placeholderEl, releaseId, instanceId)
		) {
			return
		}

		placeholderEl.outerHTML = rendered.html
		rendered.onMount?.()

		emitReleaseUpdated(renderedRelease, 'hydration')
		if (renderedRelease.data.master_year_status === 'loading') {
			void pollReleaseMasterYear(renderedRelease)
		}
	} catch (error) {
		if (!session.aborted)
			console.error('Failed to hydrate release details', error)
	} finally {
		if (hydratingReleases.get(releaseId) === session)
			hydratingReleases.delete(releaseId)
	}
}

function applyReleaseDataUpdate(release: ClientRelease): ClientRelease {
	const releaseEl = document.getElementById('release')
	if (releaseEl && !releaseMatches(releaseEl, release)) {
		return release
	}
	// Only public metadata belongs to this response. Preserve edits made while
	// the request was pending, and never target another copy of the same release.
	const current = getCachedReleaseByInstanceId(release.id)
	if (current) {
		release = {
			...current,
			data: { ...current.data, ...release.data },
			hasDetails: release.hasDetails,
			detailsStatus: release.detailsStatus,
		}
	}
	const releaseInstanceId = releaseEl?.dataset.releaseInstanceId
	const cached = releaseInstanceId
		? updateCachedReleaseByInstanceId(release, releaseInstanceId)
		: updateCachedReleaseByInstanceId(release, release.id)
	const updated = cached ?? release
	updateReleaseDetails(updated)
	emitReleaseUpdated(updated, 'details')
	return updated
}

function releaseMatches(
	releaseEl: HTMLElement,
	release: ClientRelease,
): boolean {
	return releaseMatchesIds(releaseEl, release.data?.id, release.id)
}

function releaseMatchesIds(
	releaseEl: HTMLElement,
	releaseId: number | string | undefined,
	instanceId?: number | string,
): boolean {
	if (!releaseEl.isConnected) return false

	const currentReleaseId = releaseEl.dataset.releaseId
	const currentInstanceId = releaseEl.dataset.releaseInstanceId
	const releaseIdText =
		releaseId === undefined ? undefined : String(releaseId)
	const instanceIdText =
		instanceId === undefined ? undefined : String(instanceId)

	if (currentReleaseId && releaseIdText && currentReleaseId === releaseIdText)
		return true
	if (
		currentInstanceId &&
		instanceIdText &&
		currentInstanceId === instanceIdText
	)
		return true

	return false
}

async function pollReleaseMasterYear(release: ClientRelease): Promise<void> {
	const session = getSessionSignal()
	const releaseId = Number(release.data?.id ?? release.id)
	if (!Number.isFinite(releaseId) || releaseId <= 0) return
	if (pollingMasterYears.get(releaseId) === session) return

	pollingMasterYears.set(releaseId, session)
	try {
		for (let attempt = 0; attempt < MASTER_YEAR_POLL_ATTEMPTS; attempt++) {
			if (!(await waitForMasterYearPoll(session))) return

			const details = await getReleaseDetails(releaseId)
			if (session.aborted) return
			if (!details) continue

			const merged: ClientRelease = {
				...release,
				data: { ...release.data, ...details },
				hasDetails: true,
				detailsStatus: 'ok',
			}
			const cached = applyReleaseDataUpdate(merged)
			Object.assign(release, cached)

			if (cached.data.master_year_status !== 'loading') return
		}

		const timedOut: ClientRelease = {
			...release,
			data: {
				...release.data,
				master_year_status: 'error',
			},
		}
		const cached = applyReleaseDataUpdate(timedOut)
		Object.assign(release, cached)
	} catch (error) {
		if (!session.aborted) console.error('Failed to poll master year', error)
	} finally {
		if (pollingMasterYears.get(releaseId) === session)
			pollingMasterYears.delete(releaseId)
	}
}

function waitForMasterYearPoll(session: AbortSignal): Promise<boolean> {
	return new Promise((resolve) => {
		if (session.aborted) return resolve(false)
		const finish = () => {
			window.clearTimeout(timer)
			session.removeEventListener('abort', finish)
			resolve(!session.aborted)
		}
		const timer = window.setTimeout(finish, MASTER_YEAR_POLL_INTERVAL_MS)
		session.addEventListener('abort', finish, { once: true })
	})
}

export function updateReleaseDetails(release: ClientRelease): void {
	const releaseEl = document.getElementById('release')
	if (!releaseEl) return

	const releaseDiscogsId = release.data?.id ?? release.id

	if (!releaseMatchesIds(releaseEl, releaseDiscogsId, release.id)) return

	const tracklist = releaseEl.querySelector<HTMLElement>('#release-tracklist')
	if (tracklist) {
		const signature = getTracklistSignature(release)
		if (tracklistSignatures.get(tracklist) !== signature) {
			tracklist.innerHTML =
				renderTracklist(release.data) ||
				`<li class="track">${t('release.tracklistFallback')}</li>`
			tracklistSignatures.set(tracklist, signature)
		}
	}

	const cover =
		releaseEl.querySelector<HTMLImageElement>('.cover-wrapper img')
	if (cover && release.data.cover_image) {
		cover.src = release.data.cover_image
	}

	const titleEl = releaseEl.querySelector('#release-header h2')
	if (titleEl) {
		const titleText = titleEl.querySelector<HTMLElement>('.title-text')
		if (titleText) {
			titleText.textContent = release.data.title
		} else {
			titleEl.innerHTML = releaseTitleTemplate(release.data.title)
		}
	}

	const yearEl = releaseEl.querySelector('[data-release-year]')
	if (yearEl) {
		yearEl.innerHTML =
			release.data.master_year && release.data.master_year > 0
				? String(release.data.master_year)
				: release.data.master_year_status === 'loading'
					? releaseYearLoadingTemplate(t('release.loading'))
					: ''
	}

	const infos = releaseEl.querySelector<HTMLElement>('#release-infos')
	if (infos) {
		const signature = getReleaseInfoSignature(release)
		if (releaseInfoSignatures.get(infos) !== signature) {
			infos.innerHTML = renderReleaseInfoList(release)
			releaseInfoSignatures.set(infos, signature)
		}
	}

	releaseEl.dispatchEvent(
		new CustomEvent<ClientRelease>('release:details-updated', {
			detail: release,
		}),
	)
}

function rememberRenderedDetails(
	releaseEl: HTMLElement,
	release: ClientRelease,
): void {
	const tracklist = releaseEl.querySelector<HTMLElement>('#release-tracklist')
	if (tracklist) {
		tracklistSignatures.set(tracklist, getTracklistSignature(release))
	}

	const infos = releaseEl.querySelector<HTMLElement>('#release-infos')
	if (infos) {
		releaseInfoSignatures.set(infos, getReleaseInfoSignature(release))
	}
}

function getTracklistSignature(release: ClientRelease): string {
	return JSON.stringify([
		release.detailsStatus,
		release.data.tracklist ?? null,
	])
}

function getReleaseInfoSignature(release: ClientRelease): string {
	const data = release.data
	return JSON.stringify([
		release.detailsStatus,
		data.released,
		data.country,
		data.formats,
		data.labels,
		data.genres,
		data.styles,
		data.community,
		data.notes,
		data.id,
	])
}

export function applyReleaseHistoryUpdate(
	release: ClientRelease,
	updatedHistory: ReleaseHistory,
	releaseInstanceId?: string | number | null,
): ClientRelease {
	const shouldDim = !!updatedHistory.isHidden || !!updatedHistory.hiddenUntil
	const releaseInstanceIdStr = String(
		releaseInstanceId ?? release.data?.id ?? release.id,
	)

	document
		.querySelectorAll<HTMLLIElement>('li.release')
		.forEach((element) => {
			if (element.dataset.releaseId === releaseInstanceIdStr) {
				element.classList.toggle('dimmed', shouldDim)
			}
		})
	document
		.querySelector<HTMLElement>('#release-header')
		?.classList.toggle('is-hidden-release', shouldDim)

	const historyList = document.getElementById('release-history-list')
	if (historyList) {
		historyList.innerHTML = renderHistoryList(
			updatedHistory,
			release.date_added,
		)
	}

	const updatedRelease: ClientRelease = {
		...release,
		history: updatedHistory,
	}
	const mergedRelease =
		releaseInstanceId !== null && releaseInstanceId !== undefined
			? updateCachedReleaseByInstanceId(updatedRelease, releaseInstanceId)
			: updateCachedReleaseByReleaseId(updatedRelease)
	const resolvedRelease = mergedRelease ?? updatedRelease
	Object.assign(release, resolvedRelease)
	emitReleaseUpdated(resolvedRelease, 'history')

	return resolvedRelease
}

function updateDetailStatus(
	releaseEl: HTMLElement,
	activeStatus: DetailStatus,
): void {
	;(
		Object.entries(DETAIL_STATUS_SELECTORS) as Array<[DetailStatus, string]>
	).forEach(([status, selector]) => {
		const element = releaseEl.querySelector<HTMLElement>(selector)
		if (!element) return
		element.toggleAttribute('hidden', status !== activeStatus)
	})
}
